import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Dashboard as DashboardIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { redirect, useNavigate, useParams, useLocation } from 'react-router-dom';
import EnclosureService from '../../../services/enclosureService';
import StageService from '../../../services/stageService';
import { getDANC } from '../../../services/apiCalibration';
import { generateAndSavePreviewReport } from '../../../services/previewReportService';
import { decryptText, decryptAppId } from '../../../utils/encryption';

const FinalAssessorSubmitSection = () => {
  const navigate = useNavigate();
  
  // Router hooks for encrypted parameters
  const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
  const location = useLocation();

  // Extract encrypted parameters from URL query params or route params
  const getOrgId = useCallback(() => {
    try {
      // First try to get from URL query parameters
      const urlParams = new URLSearchParams(location.search);
      let orgIdParam = urlParams.get('orgId');
      
      if (orgIdParam) {
        // URLSearchParams automatically decodes once, but if it's double-encoded, decode again
        // Check if it contains encoded characters like %2F or %2B
        if (orgIdParam.includes('%')) {
          orgIdParam = decodeURIComponent(orgIdParam);
        }
        console.log('🔐 Encrypted orgId from URL:', orgIdParam);
        const decrypted = decryptText(orgIdParam);
        console.log('🔓 Decrypted orgId:', decrypted);
        return decrypted;
      }
      
      // Then try to get from route parameters
      if (encryptedOrgId) {
        let decoded = encryptedOrgId;
        if (decoded.includes('%')) {
          decoded = decodeURIComponent(decoded);
        }
        console.log('🔐 Encrypted orgId from route:', decoded);
        const decrypted = decryptText(decoded);
        console.log('🔓 Decrypted orgId:', decrypted);
        return decrypted;
      }
      
      return null;
    } catch (error) {
      console.error('FinalSubmitSection - Error decrypting orgId:', error);
      return null;
    }
  }, [encryptedOrgId, location.search]);

  const getAppId = useCallback(() => {
    try {
      // First try to get from URL query parameters
      const urlParams = new URLSearchParams(location.search);
      let appIdParam = urlParams.get('appId');
      
      if (appIdParam) {
        // URLSearchParams automatically decodes once, but if it's double-encoded, decode again
        // Check if it contains encoded characters like %2F or %2B
        if (appIdParam.includes('%')) {
          appIdParam = decodeURIComponent(appIdParam);
        }
        console.log('🔐 Encrypted appId from URL:', appIdParam);
        const decrypted = decryptAppId(appIdParam);
        console.log('🔓 Decrypted appId:', decrypted);
        return decrypted;
      }
      
      // Then try to get from route parameters
      if (encryptedAppId) {
        let decoded = encryptedAppId;
        if (decoded.includes('%')) {
          decoded = decodeURIComponent(decoded);
        }
        console.log('🔐 Encrypted appId from route:', decoded);
        const decrypted = decryptAppId(decoded);
        console.log('🔓 Decrypted appId:', decrypted);
        return decrypted;
      }
      
      return null;
    } catch (error) {
      console.error('FinalSubmitSection - Error decrypting appId:', error);
      return null;
    }
  }, [encryptedAppId, location.search]);
  
  const [sendToCABDialogOpen, setSendToCABDialogOpen] = useState(false);
  const [sendToNABLDialogOpen, setSendToNABLDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allSectionsAdequate, setAllSectionsAdequate] = useState(false);
  const [adequacyCheckLoading, setAdequacyCheckLoading] = useState(false);
  const [inadequateSections, setInadequateSections] = useState([]);
  const [unassessedSections, setUnassessedSections] = useState([]);

  const [applicationStage, setApplicationStage] = useState(null);
  const [decryptionError, setDecryptionError] = useState(false);

  // Get application stage from API
  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/laboratory/application-stage/${appId}`);
      const result = await response.json();
      
      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        setApplicationStage('1'); // Default to 1
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
    }
  };

  // Check if user has the required permissions and check adequacy status
  useEffect(() => {
    const appId = getAppId();
    const orgId = getOrgId();
    const userTypeId = localStorage.getItem('userTypeId') || '1';
    
    // Check if decryption failed
    if (!appId || !orgId) {
      console.error('❌ Failed to decrypt appId or orgId from URL parameters');
      setDecryptionError(true);
      setError('Failed to decrypt URL parameters. Please check the link and try again.');
      return;
    }
    
    console.log('✅ Successfully decrypted parameters:', { appId, orgId });
    setDecryptionError(false);
    
    if (appId) {
      getApplicationStage(appId);
    }
    
    // Check permissions when component mounts and when stage changes
    if (applicationStage && !(((applicationStage === '18' || applicationStage === '18b') && userTypeId === '4') || applicationStage === '18a')) {
      navigate('/main/calibration/laboratory-details');
    }
    
    // Check adequacy status when component mounts and stage is available
    if ((applicationStage === '18' || applicationStage === '18b') && localStorage.getItem('userTypeId') === '4') {
      checkAllSectionsAdequacy();
    }
  }, [navigate, applicationStage]);

  // List of all subsections within Laboratory Details that need adequacy checks
  const laboratoryDetailsSubsections = [
    'laboratory-details',
    'legal-entity-type',
    'type-of-functioning', 
    'contact-person-nabl',
    'parent-organization',
    'adverse-action-history',
    'sez-region',
    'consultancy',
    'medical-testing-facility',
    'sample-collection',
    'internal-audit-management-review',
    
  ];

  // Function to check if ANY section in the ENTIRE application is marked as inadequate
  const checkAllSectionsAdequacy = async () => {
    setAdequacyCheckLoading(true);
    const orgId = getOrgId();
    const appId = getAppId();
    
    try {
      
      // Get scope records with matching source IDs
      const scopeResponse = await fetch(`${process.env.REACT_APP_API_URL}/da-nc/scope-matching?orgid=${orgId}&appid=${appId}`);
      const scopeData = await scopeResponse.json();
      
      // Get equipment records with matching source IDs
      const equipmentResponse = await fetch(`${process.env.REACT_APP_API_URL}/da-nc/equipment-matching?orgid=${orgId}&appid=${appId}`);
      const equipmentData = await equipmentResponse.json();
      
      // Get reference material records with matching source IDs
      const refMaterialResponse = await fetch(`${process.env.REACT_APP_API_URL}/da-nc/reference-materials-matching?orgid=${orgId}&appid=${appId}`);
      const refMaterialData = await refMaterialResponse.json();
      
      // Get non-analytical instrument records with matching source IDs
      const nonAnalyticalResponse = await fetch(`${process.env.REACT_APP_API_URL}/da-nc/nonanalytical-matching?orgid=${orgId}&appid=${appId}`);
      const nonAnalyticalData = await nonAnalyticalResponse.json();
      
      // Check if any of the special sections have inadequate items
      const hasInadequateScope = scopeData.success && scopeData.data.some(item => item.InformationStatus === 'Inadequate');
      const hasInadequateEquipment = equipmentData.success && equipmentData.data.some(item => item.InformationStatus === 'Inadequate');
      const hasInadequateRefMaterials = refMaterialData.success && refMaterialData.data.some(item => item.InformationStatus === 'Inadequate');
      const hasInadequateNonAnalytical = nonAnalyticalData.success && nonAnalyticalData.data.some(item => item.InformationStatus === 'Inadequate');
      
      // Check for truly inadequate items (not "To be verify") in special sections
      const hasTrulyInadequateScope = scopeData.success && scopeData.data.some(item => {
        const status = item.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const hasTrulyInadequateEquipment = equipmentData.success && equipmentData.data.some(item => {
        const status = item.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const hasTrulyInadequateRefMaterials = refMaterialData.success && refMaterialData.data.some(item => {
        const status = item.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const hasTrulyInadequateNonAnalytical = nonAnalyticalData.success && nonAnalyticalData.data.some(item => {
        const status = item.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });

      // If any special section has truly inadequate items (not "To be verify"), we don't need to check regular sections
      if (hasTrulyInadequateScope || hasTrulyInadequateEquipment || hasTrulyInadequateRefMaterials || hasTrulyInadequateNonAnalytical) {
        const inadequateDescriptions = [];
        
        // Add scope inadequacies (only truly inadequate, skip "To be verify")
        if (hasTrulyInadequateScope) {
          inadequateDescriptions.push(...scopeData.data
            .filter(item => {
              const status = item.InformationStatus || '';
              return status === 'Inadequate' && 
                     status.toLowerCase() !== 'to be verify' && 
                     status.toLowerCase() !== 'to be verified';
            })
            .map(item => ({
              type: 'Scope',
              name: item.SectionName,
              id: item.scope_id,
              observation: item.Observation
            }))
          );
        }
        
        // Add equipment inadequacies (only truly inadequate, skip "To be verify")
        if (hasTrulyInadequateEquipment) {
          inadequateDescriptions.push(...equipmentData.data
            .filter(item => {
              const status = item.InformationStatus || '';
              return status === 'Inadequate' && 
                     status.toLowerCase() !== 'to be verify' && 
                     status.toLowerCase() !== 'to be verified';
            })
            .map(item => ({
              type: 'Equipment',
              name: item.equipment_name || item.SectionName,
              id: item.equipment_id,
              observation: item.Observation
            }))
          );
        }
        
        // Add reference material inadequacies (only truly inadequate, skip "To be verify")
        if (hasTrulyInadequateRefMaterials) {
          inadequateDescriptions.push(...refMaterialData.data
            .filter(item => {
              const status = item.InformationStatus || '';
              return status === 'Inadequate' && 
                     status.toLowerCase() !== 'to be verify' && 
                     status.toLowerCase() !== 'to be verified';
            })
            .map(item => ({
              type: 'Reference Material',
              name: item.reference_material_name || item.SectionName,
              id: item.ref_material_id,
              observation: item.Observation
            }))
          );
        }
        
        // Add non-analytical instrument inadequacies (only truly inadequate, skip "To be verify")
        if (hasTrulyInadequateNonAnalytical) {
          inadequateDescriptions.push(...nonAnalyticalData.data
            .filter(item => {
              const status = item.InformationStatus || '';
              return status === 'Inadequate' && 
                     status.toLowerCase() !== 'to be verify' && 
                     status.toLowerCase() !== 'to be verified';
            })
            .map(item => ({
              type: 'Non-Analytical Instrument',
              name: item.InstrumentName || item.SectionName,
              id: item.nonanalytical_id,
              observation: item.Observation
            }))
          );
        }
        
        // Just add section names for truly inadequate sections
        const inadequateSectionNames = [];
        if (hasTrulyInadequateScope) inadequateSectionNames.push("Scope Details");
        if (hasTrulyInadequateEquipment) inadequateSectionNames.push("Equipment Details");
        if (hasTrulyInadequateRefMaterials) inadequateSectionNames.push("Reference Material Details");
        if (hasTrulyInadequateNonAnalytical) inadequateSectionNames.push("Non-Analytical Instrument Details");
        
        setInadequateSections(inadequateSectionNames);
        setAllSectionsAdequate(false);
        setUnassessedSections([]);
        setAdequacyCheckLoading(false);
        return;
      }
      
      // If all special sections are adequate, check the remaining sections
      const adequateResponse = await getDANC({
        orgid: orgId,
        appid: appId,
        informationStatus: 'Adequate',
        limit: 600
      });
      
      const inadequateResponse = await getDANC({
        orgid: orgId,
        appid: appId,
        informationStatus: 'Inadequate',
        limit: 600
      });
      
      // Process data from all responses
      const adequateData = adequateResponse.data.success ? adequateResponse.data.data || [] : [];
      const inadequateData = inadequateResponse.data.success ? inadequateResponse.data.data || [] : [];
      
      // Process specific section data with source ID matching
      const scopeRecords = scopeData.success ? scopeData.data || [] : [];
      const equipmentRecords = equipmentData.success ? equipmentData.data || [] : [];
      const refMaterialRecords = refMaterialData.success ? refMaterialData.data || [] : [];
      const nonAnalyticalRecords = nonAnalyticalData.success ? nonAnalyticalData.data || [] : [];
      
      // Find inadequate records from specific sections (excluding "To be verify" status)
      const inadequateScopeRecords = scopeRecords.filter(record => {
        const status = record.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const inadequateEquipmentRecords = equipmentRecords.filter(record => {
        const status = record.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const inadequateRefMaterialRecords = refMaterialRecords.filter(record => {
        const status = record.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      const inadequateNonAnalyticalRecords = nonAnalyticalRecords.filter(record => {
        const status = record.InformationStatus || '';
        return status === 'Inadequate' && 
               status.toLowerCase() !== 'to be verify' && 
               status.toLowerCase() !== 'to be verified';
      });
      
      // Normalize subsection names (trim and lowercase) for comparison
      const normalizeSubsectionName = (name) => {
        return name ? name.trim().toLowerCase() : '';
      };
      
      const normalizedLabDetailsSubsections = laboratoryDetailsSubsections.map(s => normalizeSubsectionName(s));
      
      // Filter out scope, equipment, reference material, and non-analytical sections from general NC checks
      const excludedSections = ['Scope', 'Equipment', 'Reference Material', 'Non-Analytical', 'NonAnalytical', 'Non Analytical'];
      
      // Check for inadequate sections in the ENTIRE application (excluding special sections and "To be verify" status)
      const allInadequateSubsectionNames = inadequateData
        .filter(record => {
          const sectionName = record.SectionName || record.SubsectionName;
          const status = record.InformationStatus || '';
          // Only include truly inadequate items (not "To be verify")
          return status === 'Inadequate' && 
                 status.toLowerCase() !== 'to be verify' && 
                 status.toLowerCase() !== 'to be verified' &&
                 sectionName && !excludedSections.some(excluded => 
                   sectionName.toLowerCase().includes(excluded.toLowerCase())
                 );
        })
        .map(record => record.SubsectionName || record.SectionName);
      
      const uniqueAllInadequateNames = [...new Set(allInadequateSubsectionNames)];
      
      // For Laboratory Details, treat "To be verify" as "Adequate"
      const adequateLabSubsections = adequateData
        .filter(record => {
          if (!record.SubsectionName) return false;
          const normalized = normalizeSubsectionName(record.SubsectionName);
          const status = record.InformationStatus || '';
          const isMatch = normalizedLabDetailsSubsections.includes(normalized);
          const isStatusAcceptable = status === 'Adequate' || 
                                   status === 'To be Verify' || 
                                   status === 'To be verify' || 
                                   status.toLowerCase() === 'to be verified';
          return isMatch && isStatusAcceptable;
        })
        .map(record => normalizeSubsectionName(record.SubsectionName));
      
      // For Laboratory Details, only consider explicitly "Inadequate" status
      const inadequateLabSubsections = inadequateData
        .filter(record => {
          if (!record.SubsectionName) return false;
          const normalized = normalizeSubsectionName(record.SubsectionName);
          const status = record.InformationStatus || '';
          const isMatch = normalizedLabDetailsSubsections.includes(normalized);
          // Only include if explicitly marked as "Inadequate"
          return isMatch && status === 'Inadequate';
        })
        .map(record => normalizeSubsectionName(record.SubsectionName));
      
      // Include any "To be verify" sections in the assessed list
      const toBeVerifyResponse = await getDANC({
        orgid: orgId,
        appid: appId,
        informationStatus: 'To be verify',
        limit: 600
      });
      
      const toBeVerifyData = toBeVerifyResponse.data.success ? toBeVerifyResponse.data.data || [] : [];
      
      // Get sections with "To be verify" status
      const toBeVerifyLabSubsections = toBeVerifyData
        .filter(record => {
          if (!record.SubsectionName) return false;
          const normalized = normalizeSubsectionName(record.SubsectionName);
          return normalizedLabDetailsSubsections.includes(normalized);
        })
        .map(record => normalizeSubsectionName(record.SubsectionName));
      
      // Combine all assessed sections including "To be verify"
      const assessedLabSubsections = [...new Set([
        ...adequateLabSubsections, 
        ...inadequateLabSubsections,
        ...toBeVerifyLabSubsections
      ])];
      
      // Find unassessed laboratory detail subsections
      const unassessedLabSubsections = laboratoryDetailsSubsections.filter(
        subsection => !assessedLabSubsections.includes(normalizeSubsectionName(subsection))
      );
      
      // Create descriptions for inadequate records (only sections names, no IDs)
      const inadequateDescriptions = [];
      
      // Only add scope inadequacies if they have truly inadequate items
      if (hasTrulyInadequateScope) {
        inadequateDescriptions.push("Scope Details");
      }
      
      // Only add equipment inadequacies if they have truly inadequate items
      if (hasTrulyInadequateEquipment) {
        inadequateDescriptions.push("Equipment Details");
      }
      
      // Only add reference material inadequacies if they have truly inadequate items
      if (hasTrulyInadequateRefMaterials) {
        inadequateDescriptions.push("Reference Material Details");
      }
      
      // Only add non-analytical instrument inadequacies if they have truly inadequate items
      if (hasTrulyInadequateNonAnalytical) {
        inadequateDescriptions.push("Non-Analytical Instrument Details");
      }
      
      // Combine all inadequate sections (only section names)
      const allInadequateSections = [
        ...uniqueAllInadequateNames,
        ...inadequateDescriptions
      ].filter(Boolean); // Remove any empty values
      
      // Store ALL inadequate sections for display
      setInadequateSections(Array.from(new Set(allInadequateSections))); // Remove duplicates
      setUnassessedSections(unassessedLabSubsections);
      
      // Check if any section is truly inadequate (not "To be verify")
      const hasAnyTrulyInadequateSection = uniqueAllInadequateNames.length > 0 || inadequateDescriptions.length > 0;
      const allLabSubsectionsAssessed = unassessedLabSubsections.length === 0;
      const allAdequate = !hasAnyTrulyInadequateSection && allLabSubsectionsAssessed;
      
      setAllSectionsAdequate(allAdequate);
       
    } catch (error) {
      console.error('Error checking adequacy status:', error);
      setError('Failed to check adequacy status. Please try again.');
      setAllSectionsAdequate(false);
      setInadequateSections([]);
      setUnassessedSections([]);
    } finally {
      setAdequacyCheckLoading(false);
    }
  };

  const handleNoNCFound = async () => {
    setLoading(true);
    setError(null);

    try {
      const orgId = getOrgId();
      const appId = getAppId();
      const labTypeId = '3'; // Medical lab type ID (adjust if needed)

      // Step 1: Generate the complete PDF
      setSuccess('Generating complete preview report PDF...');
     
      
      // Step 2: Upload to S3
      setSuccess('Preview report generated. Uploading to S3...');
  
      // Step 3: Save to database
      setSuccess('Uploaded to S3. Saving to database...');
// Execute all steps
      const reportResult = await generateAndSavePreviewReport(
        appId, 
        orgId, 
        labTypeId, 
        true // Include observations
      );
      
      setSuccess(`Preview report saved successfully (${(reportResult.size / 1024).toFixed(2)} KB). Updating application stage...`);

      // Step 4: Update the stage to 5a for No NC Found
      await StageService.updateStageForAction('NO_NC_FOUND');
      
      setSuccess('No NC found has been recorded successfully with preview report');
      
      // Redirect to dashboard after recording
      setTimeout(() => {
        window.location.href = '/main/do/dashboard';
      }, 2000);
    } catch (error) {
      console.error('❌ Error updating stage for No NC Found:', error);
      setError(error.message || 'Failed to update stage for No NC Found');
    } finally {
      setLoading(false);
    }
  };
  /**
   * Handle Send to CAB action
   * This function:
   * 1. Generates a preview report PDF with observations
   * 2. Uploads the PDF to S3
   * 3. Saves the PDF path to ApplicationEnclosure table in database
   * 4. Updates the application stage to "Send to CAB"
   */
  const handleSendToCAB = async () => {
    setLoading(true);
    setError(null);

    try {
      const orgId = getOrgId();
      const appId = getAppId();
      const labTypeId = '3'; // Medical lab type ID (adjust if needed)

      // Step 1: Generate the complete PDF
      setSuccess('Generating complete preview report PDF...');
      
      // Step 2: Upload to S3
      setSuccess('Preview report generated. Uploading to S3...');
      
      // Step 3: Save to database
      setSuccess('Uploaded to S3. Saving to database...');
      
      // Execute all steps
      const reportResult = await generateAndSavePreviewReport(
        appId, 
        orgId, 
        labTypeId, 
        true // Include observations
      );
      
      setSuccess(`Preview report saved successfully (${(reportResult.size / 1024).toFixed(2)} KB). Updating application stage...`);

      // Step 4: Update the stage
      await StageService.updateStageForAction('SEND_TO_CAB');

      setSuccess('Application sent to CAB successfully with preview report!');
      setSendToCABDialogOpen(false);
      
      // Optionally redirect after a delay
      setTimeout(() => {
        navigate('/main/do/dashboard');
      }, 2000);
    } catch (error) {
      console.error('❌ Error sending to CAB:', error);
      setError(error.message || 'Failed to send application to CAB. Please try again.');
    } finally {
      setLoading(false);
    }
  };
const handleSendToNABL = async () => {
    setLoading(true);
    setError(null);

    try {
      // Update the stage to 5a for No NC Found
      await StageService.updateStageForAction('SEND_TO_NABL');

      setSuccess('Application sent to NABL successfully');

      // Redirect to dashboard after recording
      setTimeout(() => {
        navigate('/main/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error updating stage for No NC Found:', error);
      setError(error.message || 'Failed to update stage for No NC Found');
    } finally {
      setLoading(false);
    }
  };
  const handleBackToDashboard = () => {
    navigate('/main/dashboard');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#00024c', fontWeight: 600 }}>
        Final Submit
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Your application is ready for final submission. Please choose one of the following options:
      </Typography>

      {/* Decryption Error Message */}
      {decryptionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Failed to decrypt URL parameters
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The orgId and appId parameters could not be decrypted. Please ensure you're using the correct link.
          </Typography>
        </Alert>
      )}

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Stage 18 or 18b Buttons - Show when stage is 18 or 18b AND userTypeId is 4 */}
          {(applicationStage === '18' || applicationStage === '18b') && localStorage.getItem('userTypeId') === '4' && (
            <>
              {/* Show different content based on assessment status */}
              {adequacyCheckLoading && (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        Checking adequacy status of all laboratory sections...
                      </Typography>
                    </Box>
                  </Alert>
                </Box>
              )}

              {/* Show message if laboratory details subsections are unassessed */}
              {!adequacyCheckLoading && unassessedSections.length > 0 && (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Please complete the assessment for all Laboratory Details subsections before proceeding.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        The following Laboratory Details subsections need to be marked as either "Adequate" or "Inadequate":
                      </Typography>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {unassessedSections.map(section => (
                          <li key={section} style={{ marginBottom: '4px' }}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {section ? section.replace(/-/g, ' ') : 'Unknown Section'}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Alert>
                </Box>
              )}

              {/* Send to CAB Button - Show when ANY section in the entire application is inadequate */}
              {!adequacyCheckLoading && unassessedSections.length === 0 && inadequateSections.length > 0 && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#00024c' }}>
                    Send Application to CAB
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Some sections in the application have been marked as inadequate. Send the application to CAB for review.
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Inadequate sections found ({inadequateSections.length}):
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px', maxHeight: '200px', overflowY: 'auto' }}>
                      {inadequateSections.map(section => (
                        <li key={section} style={{ marginBottom: '4px' }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {section.replace(/-/g, ' ')}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Alert>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={() => setSendToCABDialogOpen(true)}
                    sx={{
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                      },
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Send to CAB
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Close NC Button - Show when NO section in the entire application is inadequate (all are adequate) */}
              {!adequacyCheckLoading && allSectionsAdequate && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#00024c' }}>
                    Close Assessment
                  </Typography>
                  
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      All sections in the application have been assessed and no inadequate sections found. You can now close the assessment.
                    </Typography>
                  </Alert>
                  
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    onClick={handleNoNCFound}
                    disabled={loading}
                    sx={{
                      backgroundColor: '#4caf50',
                      '&:hover': {
                      backgroundColor: '#45a049',
                      },
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {loading ? 'Processing...' : 'Close NC'}
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

            </>
          )}

          {/* Stage 18a Button - Show when stage is 18a only */}
          {applicationStage === '18a' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#00024c' }}>
                Send to NABL
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Send your corrected application back to NABL for further review.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                onClick={() => setSendToNABLDialogOpen(true)}
                sx={{
                  backgroundColor: '#ff9800',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                  },
                  px: 4,
                  py: 1.5,
                }}
              >
                Send to NABL
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Send to CAB Dialog */}
      <Dialog
        open={sendToCABDialogOpen}
        onClose={() => !loading && setSendToCABDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send to CAB</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Enter your password to send the application to CAB.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSendToCABDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendToCAB}
            variant="contained"
            disabled={loading || !password.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send to CAB'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send to NABL Dialog */}
      <Dialog
        open={sendToNABLDialogOpen}
        onClose={() => !loading && setSendToNABLDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send to NABL</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Enter your password to send the application to NABL. This will update the stage back to 18.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSendToNABLDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendToNABL}
            variant="contained"
            disabled={loading || !password.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            {loading ? 'Sending...' : 'Send to NABL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalAssessorSubmitSection;